import * as openpgp from "openpgp";
import type { DecryptedFileMeta } from "@sss/shared";

/**
 * All encryption here is symmetric (password-based), never public-key —
 * there are no accounts or keypairs. The "password" is a random passphrase
 * generated per secret and carried only in the share link's URL fragment,
 * which browsers never send to the server. The server therefore never sees
 * plaintext or the passphrase.
 */

export function generatePassphrase(): string {
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  return bytesToBase64Url(bytes);
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function buildShareLink(id: string, passphrase: string): string {
  return `${window.location.origin}/s/${id}#${passphrase}`;
}

export async function encryptText(plaintext: string, passphrase: string): Promise<string> {
  const message = await openpgp.createMessage({ text: plaintext });
  const ciphertext = await openpgp.encrypt({
    message,
    passwords: [passphrase],
    format: "armored",
  });
  return ciphertext as string;
}

export async function decryptText(armoredCiphertext: string, passphrase: string): Promise<string> {
  const message = await openpgp.readMessage({ armoredMessage: armoredCiphertext });
  const { data } = await openpgp.decrypt({
    message,
    passwords: [passphrase],
    format: "utf8",
  });
  return data as string;
}

export async function encryptMeta(meta: DecryptedFileMeta, passphrase: string): Promise<string> {
  return encryptText(JSON.stringify(meta), passphrase);
}

export async function decryptMeta(armoredMeta: string, passphrase: string): Promise<DecryptedFileMeta> {
  const json = await decryptText(armoredMeta, passphrase);
  return JSON.parse(json) as DecryptedFileMeta;
}

/**
 * Streams-encrypts a File without ever holding the whole plaintext (or the
 * whole ciphertext) in memory at once. Returns a ReadableStream of
 * ciphertext bytes. `lib/api.ts` currently buffers this into a Blob before
 * upload (see the comment there for why) rather than piping it directly
 * into a streamed fetch body, but encryption itself still never buffers
 * the plaintext.
 */
export async function encryptFileStream(
  file: File,
  passphrase: string,
): Promise<ReadableStream<Uint8Array>> {
  const message = await openpgp.createMessage({ binary: file.stream() });
  const encrypted = await openpgp.encrypt({
    message,
    passwords: [passphrase],
    format: "binary",
  });
  return encrypted as unknown as ReadableStream<Uint8Array>;
}

/**
 * Streams-decrypts a ciphertext ReadableStream (e.g. a fetch Response body)
 * back into plaintext bytes, again without buffering the whole file.
 */
export async function decryptFileStream(
  ciphertextStream: ReadableStream<Uint8Array>,
  passphrase: string,
): Promise<ReadableStream<Uint8Array>> {
  const message = await openpgp.readMessage({ binaryMessage: ciphertextStream });
  const { data } = await openpgp.decrypt({
    message,
    passwords: [passphrase],
    format: "binary",
  });
  return data as unknown as ReadableStream<Uint8Array>;
}
